<template>
  <!-- eslint-disable vue/max-attributes-per-line -->
  <div v-on-clickaway="closePopover" class="queue-indicator">
    <button
      ref="pill"
      class="queue-pill"
      :class="{ 'is-empty': waitingCount === 0, 'is-open': isPopoverOpen }"
      type="button"
      :title="pillTitle"
      @click="togglePopover"
    >
      <span class="queue-metric">
        <svg class="queue-ic" viewBox="0 0 24 24" aria-hidden="true">
          <path
            d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8ZM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
          />
        </svg>
        <b>{{ waitingCount }}</b>
      </span>
      <span v-if="waitingCount > 0" class="queue-sep" aria-hidden="true" />
      <span v-if="waitingCount > 0" class="queue-metric queue-timer">
        <svg class="queue-ic" viewBox="0 0 24 24" aria-hidden="true">
          <circle cx="12" cy="13" r="8" />
          <path d="M12 9v4l2 2M9 2h6M12 5V2" />
        </svg>
        <b>{{ oldestElapsedLabel }}</b>
      </span>
    </button>

    <div
      v-if="isPopoverOpen"
      ref="popover"
      class="queue-popover"
      :style="popoverStyle"
    >
      <div class="queue-popover__head">
        {{ $t('CHAT_LIST.QUEUE.TITLE') }}
        <span class="queue-popover__count">{{ waitingCount }}</span>
      </div>
      <div class="queue-popover__body">
        <div v-if="uiFlags.isFetchingList" class="queue-popover__state">
          {{ $t('CHAT_LIST.QUEUE.LOADING') }}
        </div>
        <div v-else-if="!list.length" class="queue-popover__state">
          {{ $t('CHAT_LIST.QUEUE.EMPTY') }}
        </div>
        <ul v-else class="queue-popover__list">
          <li v-for="item in list" :key="item.id">
            <button
              type="button"
              class="queue-row"
              @click="goToConversation(item)"
            >
              <span class="queue-row__name text-truncate">
                {{ item.contact_name || $t('CHAT_LIST.QUEUE.UNKNOWN') }}
              </span>
              <span class="queue-row__time">
                {{ elapsedLabel(item.waiting_since) }}
              </span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mixin as clickaway } from 'vue-clickaway';
import router from '../../../routes';
import { frontendURL, conversationUrl } from '../../../helper/URLHelper';

const STATS_POLL_MS = 20000;

export default {
  mixins: [clickaway],
  props: {
    inboxId: {
      type: [String, Number],
      default: 0,
    },
    accountId: {
      type: [String, Number],
      default: 0,
    },
  },
  data() {
    return {
      isPopoverOpen: false,
      now: Math.floor(Date.now() / 1000),
      statsTimer: null,
      tickTimer: null,
      popoverStyle: {},
    };
  },
  computed: {
    ...mapGetters({
      waitingCount: 'queueStats/getWaitingCount',
      oldestWaitingSince: 'queueStats/getOldestWaitingSince',
      list: 'queueStats/getQueueList',
      uiFlags: 'queueStats/getUIFlags',
    }),
    scopedInboxId() {
      return this.inboxId || undefined;
    },
    oldestElapsedLabel() {
      return this.elapsedLabel(this.oldestWaitingSince);
    },
    pillTitle() {
      if (!this.waitingCount) {
        return this.$t('CHAT_LIST.QUEUE.EMPTY');
      }
      return this.$t('CHAT_LIST.QUEUE.PILL_TITLE', {
        count: this.waitingCount,
        time: this.oldestElapsedLabel,
      });
    },
  },
  watch: {
    scopedInboxId() {
      this.fetchStats();
      if (this.isPopoverOpen) {
        this.fetchList();
      }
    },
  },
  mounted() {
    this.fetchStats();
    this.statsTimer = setInterval(this.fetchStats, STATS_POLL_MS);
    this.tickTimer = setInterval(() => {
      this.now = Math.floor(Date.now() / 1000);
    }, 1000);
    // A pílula fica na barra superior (fixed) e não se move com o scroll,
    // então NÃO fechamos no scroll (senão rolar a própria lista fecharia).
    // Só reposicionamos ao redimensionar a janela.
    window.addEventListener('resize', this.onResize);
  },
  beforeDestroy() {
    clearInterval(this.statsTimer);
    clearInterval(this.tickTimer);
    window.removeEventListener('resize', this.onResize);
  },
  methods: {
    updatePopoverPosition() {
      const el = this.$refs.pill;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // fixed: relativo à viewport, escapa do clipping de containers com
      // overflow. Ancorado ao gatilho e limitado às bordas da viewport
      // (a pílula fica no canto direito da barra superior).
      const width = 260;
      let left = rect.left;
      if (left + width > window.innerWidth - 8) {
        left = window.innerWidth - width - 8;
      }
      if (left < 8) left = 8;
      this.popoverStyle = {
        top: `${Math.round(rect.bottom + 6)}px`,
        left: `${Math.round(left)}px`,
      };
    },
    fetchStats() {
      this.$store.dispatch('queueStats/fetchStats', {
        inboxId: this.scopedInboxId,
      });
    },
    fetchList() {
      this.$store.dispatch('queueStats/fetchList', {
        inboxId: this.scopedInboxId,
      });
    },
    togglePopover() {
      this.isPopoverOpen = !this.isPopoverOpen;
      if (this.isPopoverOpen) {
        this.updatePopoverPosition();
        this.fetchList();
      }
    },
    closePopover() {
      this.isPopoverOpen = false;
    },
    onResize() {
      if (this.isPopoverOpen) this.updatePopoverPosition();
    },
    elapsedLabel(since) {
      if (!since) return '--:--';
      const seconds = Math.max(0, this.now - since);
      const h = Math.floor(seconds / 3600);
      const m = Math.floor((seconds % 3600) / 60);
      const s = seconds % 60;
      const pad = value => String(value).padStart(2, '0');
      return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
    },
    goToConversation(item) {
      const path = frontendURL(
        conversationUrl({
          accountId: this.accountId,
          activeInbox: this.inboxId || undefined,
          id: item.id,
        })
      );
      this.closePopover();
      router.push({ path });
    },
  },
};
</script>

<style lang="scss" scoped>
.queue-indicator {
  position: relative;
  display: inline-flex;
}

.queue-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 38px;
  padding: 0 12px;
  border: 1px solid var(--tb-border, var(--color-border));
  border-radius: 10px;
  background: var(--tb-surface-2, var(--color-surface));
  color: var(--tb-text, var(--color-body));
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

  b {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }

  &:hover,
  &.is-open {
    background: var(--tb-brand-050, var(--color-woot-tint));
    border-color: var(--tb-brand, var(--color-woot));
    color: var(--tb-brand, var(--color-woot));
  }

  &.is-empty {
    color: var(--tb-text-3, var(--s-500));

    &:hover,
    &.is-open {
      background: var(--tb-surface-2, var(--s-75));
      border-color: var(--tb-border-strong, var(--color-border));
      color: var(--tb-text-2, var(--s-600));
    }
  }
}

.queue-metric {
  display: inline-flex;
  align-items: center;
  gap: var(--space-micro);
}

.queue-ic {
  width: 16px;
  height: 16px;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.queue-sep {
  width: 1px;
  height: 16px;
  background: currentColor;
  opacity: 0.25;
}

// Responsivo — em desktop com telas menores mostra só a quantidade
// na fila; o tempo de espera passa a aparecer no hover (atributo
// title da pílula: "{n} na fila · 1º aguardando há {tempo}"). O
// clique continua abrindo o popover com a lista. ADITIVO: telas
// grandes seguem mostrando contador + cronômetro inline.
@media (max-width: 1366px) {
  .queue-sep,
  .queue-timer {
    display: none;
  }
}

.queue-timer b {
  min-width: 5.6rem;
  text-align: left;
}

.queue-popover {
  position: fixed;
  z-index: var(--z-index-very-high, 9999);
  width: 26rem;
  max-width: 80vw;
  background: var(--tb-surface, var(--color-surface));
  border: 1px solid var(--tb-border, var(--color-border));
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
  overflow: hidden;
}

.queue-popover__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small) var(--space-normal);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-small);
  color: var(--tb-text, var(--s-800));
  border-bottom: 1px solid var(--tb-border, var(--color-border-light));
}

.queue-popover__count {
  background: var(--tb-brand, var(--color-woot));
  color: var(--tb-brand-ink, var(--white));
  font-size: var(--font-size-mini);
  font-weight: var(--font-weight-bold);
  min-width: 1.8rem;
  height: 1.8rem;
  border-radius: var(--border-radius-rounded);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 var(--space-smaller);
}

.queue-popover__body {
  max-height: 32rem;
  overflow-y: auto;
}

.queue-popover__state {
  padding: var(--space-normal);
  text-align: center;
  color: var(--tb-text-3, var(--s-500));
  font-size: var(--font-size-small);
}

.queue-popover__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.queue-row {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-small);
  padding: var(--space-small) var(--space-normal);
  background: transparent;
  border: 0;
  border-bottom: 1px solid var(--tb-border, var(--color-border-light));
  cursor: pointer;
  text-align: left;

  &:hover {
    background: var(--tb-surface-2, var(--s-75));
  }
}

.queue-row__name {
  font-size: var(--font-size-small);
  color: var(--tb-text, var(--color-body));
  flex: 1;
  min-width: 0;
}

.queue-row__time {
  font-size: var(--font-size-mini);
  font-variant-numeric: tabular-nums;
  color: var(--tb-text-2, var(--s-600));
  flex: none;
}
</style>
